import * as THREE from 'three';
import type { CarStats } from '../game/cars/CarTypes';

/**
 * RealisticCarRenderer - Erstellt bessere Auto-Modelle mit realistischen Proportionen
 */
export class RealisticCarRenderer {
  /**
   * Erstellt ein 3D-Auto-Modell mit realistischen Proportionen
   */
  static createCarMesh(stats: CarStats, colorHex: string, neonColorHex: string, neonIntensity: number): THREE.Group {
    const group = new THREE.Group();

    // Skalierungsfaktor für bessere Sichtbarkeit
    const scale = 1.2;

    // Hauptkarosserie (Box mit abgerundeter Form)
    const bodyGeometry = new THREE.BoxGeometry(2 * scale, 1.2 * scale, 4.5 * scale);
    const bodyMaterial = new THREE.MeshStandardMaterial({
      color: colorHex,
      metalness: 0.6,
      roughness: 0.4,
      emissive: 0x000000
    });
    const bodyMesh = new THREE.Mesh(bodyGeometry, bodyMaterial);
    bodyMesh.position.set(0, 0.6 * scale, 0);
    bodyMesh.castShadow = true;
    bodyMesh.receiveShadow = true;
    group.add(bodyMesh);

    // Fenster (Glas-Effekt)
    const windowGeometry = new THREE.BoxGeometry(1.8 * scale, 0.6 * scale, 1.5 * scale);
    const windowMaterial = new THREE.MeshStandardMaterial({
      color: 0x4488ff,
      metalness: 0.9,
      roughness: 0.1,
      transparent: true,
      opacity: 0.6
    });
    const windowMesh = new THREE.Mesh(windowGeometry, windowMaterial);
    windowMesh.position.set(0, 1.1 * scale, -0.3 * scale);
    group.add(windowMesh);

    // Reifen (4x vorne und hinten)
    this.createWheels(group, scale);

    // Neon-Effekt (seitliche Beleuchtung)
    if (neonIntensity > 0) {
      this.addNeonAccents(group, neonColorHex, neonIntensity, scale);
    }

    // Lichter vorne und hinten
    this.addHeadlights(group, scale);
    this.addTaillights(group, scale);

    return group;
  }

  private static createWheels(group: THREE.Group, scale: number = 1): void {
    const wheelRadius = 0.5 * scale;
    const wheelWidth = 0.4 * scale;
    const wheelGeometry = new THREE.CylinderGeometry(wheelRadius, wheelRadius, wheelWidth, 32);
    const wheelMaterial = new THREE.MeshStandardMaterial({
      color: 0x111111,
      metalness: 0.8,
      roughness: 0.3
    });

    // Positionen: 2 vorne, 2 hinten
    const wheelPositions = [
      { x: -0.8, z: 1.2 }, // vorne links
      { x: 0.8, z: 1.2 },  // vorne rechts
      { x: -0.8, z: -1.2 }, // hinten links
      { x: 0.8, z: -1.2 }   // hinten rechts
    ];

    wheelPositions.forEach((pos) => {
      const wheel = new THREE.Mesh(wheelGeometry, wheelMaterial);
      wheel.rotation.z = Math.PI / 2;
      wheel.position.set(pos.x, 0.5, pos.z);
      wheel.castShadow = true;
      wheel.receiveShadow = true;
      group.add(wheel);
    });
  }

  private static addNeonAccents(group: THREE.Group, neonColor: string, intensity: number, scale: number = 1): void {
    // Neon-Streifen seitlich
    const neonGeometry = new THREE.PlaneGeometry(0.1 * scale, 4 * scale);
    const neonMaterial = new THREE.MeshStandardMaterial({
      color: neonColor,
      emissive: neonColor,
      emissiveIntensity: intensity * 2,
      metalness: 0,
      roughness: 0.2
    });

    // Linke Seite
    const neonLeft = new THREE.Mesh(neonGeometry, neonMaterial);
    neonLeft.position.set(-1.05 * scale, 0.6 * scale, 0);
    neonLeft.rotation.y = 0;
    group.add(neonLeft);

    // Rechte Seite
    const neonRight = new THREE.Mesh(neonGeometry, neonMaterial);
    neonRight.position.set(1.05, 0.6, 0);
    group.add(neonRight);

    // Unten
    const neonBottom = new THREE.Mesh(neonGeometry, neonMaterial);
    neonBottom.rotation.z = Math.PI / 2;
    neonBottom.position.set(0, 0.05, 0);
    group.add(neonBottom);
  }

  private static addHeadlights(group: THREE.Group, scale: number = 1): void {
    const lightGeometry = new THREE.CircleGeometry(0.3 * scale, 16);
    const lightMaterial = new THREE.MeshStandardMaterial({
      color: 0xffff99,
      emissive: 0xffff99,
      emissiveIntensity: 0.8,
      metalness: 0,
      roughness: 0.3
    });

    // Linkes Licht
    const leftLight = new THREE.Mesh(lightGeometry, lightMaterial);
    leftLight.position.set(-0.6 * scale, 0.8 * scale, 2.3 * scale);
    leftLight.rotation.y = 0.2;
    group.add(leftLight);

    // Rechtes Licht
    const rightLight = new THREE.Mesh(lightGeometry, lightMaterial);
    rightLight.position.set(0.6 * scale, 0.8 * scale, 2.3 * scale);
    rightLight.rotation.y = -0.2;
    group.add(rightLight);
  }

  private static addTaillights(group: THREE.Group, scale: number = 1): void {
    const lightGeometry = new THREE.CircleGeometry(0.25 * scale, 16);
    const lightMaterial = new THREE.MeshStandardMaterial({
      color: 0xff3333,
      emissive: 0xff3333,
      emissiveIntensity: 0.6,
      metalness: 0,
      roughness: 0.3
    });

    // Links hinten
    const leftTail = new THREE.Mesh(lightGeometry, lightMaterial);
    leftTail.position.set(-0.7 * scale, 0.7 * scale, -2.3 * scale);
    group.add(leftTail);

    // Rechts hinten
    const rightTail = new THREE.Mesh(lightGeometry, lightMaterial);
    rightTail.position.set(0.7 * scale, 0.7 * scale, -2.3 * scale);
    group.add(rightTail);
  }
}
